/*
 * /dental-scan-viewer — открыть снимок зубов и показать его врачу.
 *
 * Сегмент занят сильными конкурентами (MyNaviScan, DentiSlice, CBCTHub,
 * DicomViewer.net), и все они продают обещание про то, куда не уходят файлы.
 * Нам этот аргумент недоступен по правилам площадки, и это хорошо: он
 * заставил искать другое отличие — ЧТО у пациента на руках и что ему нужно.
 *
 *   · панорама одной картинкой → схема зубов с номерами ПОВЕРХ снимка;
 *   · диск после томографии → все форматы сжатия, папка целиком, архив, и
 *     плоскости «сверху / спереди / сбоку» с перекрестием, как в программе
 *     клиники, но без установки;
 *   · PDF из клиники → снимки и текст заключения.
 *
 * Разбор и декодирование — loader.js (+ воркеры), отрисовка — render.js.
 */

import { loadInputs } from './loader.js';
import { paint, autoWindow, valueRange, planeImage, planeDepth } from './render.js';
import { detectArch, renderPanorama, crossSection, sampleCurve, axialMip } from './panorama.js';
import { Odontogram } from '../odontogram.js';
import { initAi } from './ai.js';
import { fromRgba } from './loader.js';
import { bitmapRgba } from './codecs.js';

const CFG = window.__SCAN || {};
const T = CFG.i18n || {};
const $ = s => document.querySelector(s);
const $$ = s => Array.from(document.querySelectorAll(s));

const state = {
    frames: [],            // 2D-кадры
    volume: null,          // объём КТ
    mode: 'frames',        // 'frames' | 'volume'
    plane: 'axial',
    index: 0,              // кадр (frames) или срез текущей плоскости
    cursor: null,          // {x, y, z} в вокселях — перекрестие плоскостей
    win: { center: 128, width: 256, invert: false },
    zoom: 1,
    pan: { x: 0, y: 0 },
    measure: null,         // {from:{x,y}, to:{x,y}} в пикселях КАРТИНКИ
    overlay: false,
    overlayBox: { x: 0.08, y: 0.18, w: 0.84 },
    busy: false,
    // Панорама из КТ: дуга, окно по высоте, картинка, точка поперечного среза.
    pano: null,
};

let chart = null;
let ai = null;
let tool = 'pan';
let cache = { key: '', canvas: null, img: null };

// ── запуск ──────────────────────────────────────────────────────────────

function boot() {
    /*
     * ⚠️ Через __bindUploaderFile, а не сырой addEventListener: на медленной
     * сети человек успевает выбрать файл ДО того, как отложенный скрипт
     * привяжет слушатель, и событие теряется — тул стоит мёртвым. Примитив
     * доигрывает пропущенный выбор.
     */
    const up = $('#dvUpload');
    const takeFiles = (file, detail) => {
        const list = detail?.files?.length ? Array.from(detail.files) : (file ? [file] : []);
        if (list.length) open(list);
    };
    if (window.__bindUploaderFile) window.__bindUploaderFile(up, takeFiles);
    else up.addEventListener('tool-uploader:file', ev => takeFiles(ev.detail?.file || ev.detail?.files?.[0], ev.detail));

    $$('[data-adjust]').forEach(el => el.addEventListener('input', () => {
        state.win[el.dataset.adjust] = +el.value;
        draw();
    }));
    $('#dvInvert').addEventListener('change', () => { state.win.invert = $('#dvInvert').checked; draw(); });
    $('#dvOverlay').addEventListener('change', toggleOverlay);
    $('#dvAuto').addEventListener('click', () => { applyAutoWindow(); draw(); });
    $('#dvSlice').addEventListener('input', () => setIndex(+$('#dvSlice').value));
    $('#dvSave').addEventListener('click', () => save().catch(e => window.__handleError && window.__handleError(e, 'scan_save')));
    $('#dvReset').addEventListener('click', resetView);
    $('#dvPdfCopy')?.addEventListener('click', copyPdfText);
    $('#dvPanoThick')?.addEventListener('input', () => {
        if (!state.pano) return;
        state.pano.thick = +$('#dvPanoThick').value;
        $('#dvPanoThickVal').textContent = state.pano.thick;
        rebuildPano();
    });
    $('#dvPanoEdit')?.addEventListener('change', () => {
        if (!state.pano) return;
        state.pano.edit = $('#dvPanoEdit').checked;
        $('#dvPanoEditHint').hidden = !state.pano.edit;
        state.zoom = 1; state.pan = { x: 0, y: 0 }; state.measure = null;
        cache.key = '';
        if (!state.pano.edit) rebuildPano();
        applyAutoWindow();
        draw();
    });

    const canvas = $('#dvCanvas');
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onUp);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('dblclick', resetView);
    canvas.addEventListener('keydown', onKey);

    $$('[data-tool]').forEach(b => b.addEventListener('click', () => setTool(b.dataset.tool)));
    $$('[data-plane]').forEach(b => b.addEventListener('click', () => setPlane(b.dataset.plane)));
    $$('[data-overlay]').forEach(el => el.addEventListener('input', () => {
        if (el.dataset.overlay !== 'alpha') {
            state.overlayBox[el.dataset.overlay] = +el.value / 100;
            state.overlayTouched = true;
        }
        positionOverlay();
    }));

    if (typeof ResizeObserver === 'function') new ResizeObserver(() => draw()).observe($('#dvCanvasWrap'));

    if (CFG.ai) {
        ai = initAi({ T, cfg: CFG.ai, snapshot: { peek: aiPeek, take: aiSnapshot }, openSnapshot: openStashed });
    }

    if (window.__stage) window.__stage('ready');
}

// ── загрузка ────────────────────────────────────────────────────────────

async function open(files) {
    if (state.busy) return;
    state.busy = true;
    say(T.opening || '…');
    if (window.__stage) window.__stage('file_chosen', { n: files.length });

    let rep;
    try {
        rep = await loadInputs(files, {
            assets: CFG.assets || {},
            budgetBytes: memoryBudget(),
            onProgress: progress,
        });
    } catch (e) {
        state.busy = false;
        say(T.err_read_failed, 'bad');
        if (window.__handleError) window.__handleError(e, 'scan_open', { say: T.err_read_failed });
        return;
    }
    state.busy = false;

    const haveSomething = rep.frames.length || rep.volume;
    const summary = summarize(rep);
    if (!haveSomething) {
        say(summary || T.err_not_dicom, 'bad');
        if (window.__aeDiagnostic) window.__aeDiagnostic({ issue: 'scan_open_failed', kinds: [...rep.problems.keys()].join(',') || 'nothing', skipped: JSON.stringify(rep.skipped) });
        return;
    }
    say(summary, rep.problems.size ? 'warn' : '');

    state.frames = rep.frames;
    state.volume = rep.volume;
    state.pano = null;
    if ($('#dvPanoBox')) $('#dvPanoBox').hidden = true;
    state.mode = rep.volume ? 'volume' : 'frames';
    state.plane = 'axial';
    state.zoom = 1; state.pan = { x: 0, y: 0 }; state.measure = null;
    cache.key = '';

    if (rep.volume) {
        const v = rep.volume;
        state.cursor = { x: Math.floor(v.w / 2), y: Math.floor(v.h / 2), z: Math.floor(v.d / 2) };
        state.index = state.cursor.z;
    } else {
        state.index = Math.floor(rep.frames.length / 2);
    }

    $('#dvWork').hidden = false;
    $('#dvPlanes').hidden = !rep.volume;
    $('#dvPlaneHint').hidden = !rep.volume;

    const pdf = $('#dvPdf');
    if (pdf) {
        pdf.hidden = !rep.pdfText;
        $('#dvPdfText').textContent = rep.pdfText || '';
    }

    state.preNumbered = false;
    syncPlaneButtons();
    syncSlider();
    applyAutoWindow(true);
    draw();
    syncChartShape();
    if (ai) ai.refresh();

    if (window.__aeToolComplete) {
        window.__aeToolComplete({
            ok: 1,
            frames: rep.frames.length,
            volume: rep.volume ? 1 : 0,
            slices: rep.volume ? rep.volume.d : 0,
            factor: rep.downsampled || 1,
            pdf: rep.pdfText ? 1 : 0,
            archives: rep.archives,
            syntax: (rep.volume?.meta?.syntax || rep.frames.find(f => f.meta)?.meta?.syntax || '').split('.').slice(-2).join('.'),
        });
    }
}

function memoryBudget() {
    const dm = window.__memBudget ? window.__memBudget.gb() : 4;   // the one owner of «how much RAM» (WebKit has no deviceMemory)
    const ios = /iP(hone|ad|od)/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (ios) return 384 * 1048576;
    return Math.min(1536, dm * 192) * 1048576;
}

function progress(p) {
    if (p.stage === 'archive') say(fmt(T.progress_archive, { n: p.n }));
    else if (p.stage === 'headers') say(fmt(T.progress_headers, { done: p.done, total: p.total }));
    else if (p.stage === 'volume') say(fmt(T.progress_volume, { done: p.done, total: p.total }));
    else if (p.stage === 'frames') say(fmt(T.progress_frames, { done: p.done, total: p.total }));
}

function summarize(rep) {
    const parts = [];
    if (rep.volume) {
        const v = rep.volume;
        parts.push(fmt(T.opened_volume, { n: v.d, mm: (v.spacing[2]).toFixed(2).replace(/\.?0+$/, '') }));
        if (v.factor > 1) parts.push(fmt(T.volume_downsampled, { f: v.factor }));
    } else if (rep.frames.length) {
        parts.push(fmt(T.opened_frames, { n: rep.frames.length }));
    }
    const s = rep.skipped;
    const sk = [];
    if (s.dicomdir) sk.push(T.skip_dicomdir);
    if (s.viewerExe) sk.push(fmt(T.skip_viewer, { n: s.viewerExe }));
    if (s.service) sk.push(fmt(T.skip_service, { n: s.service }));
    if (s.other) sk.push(fmt(T.skip_other, { n: s.other }));
    if (sk.length) parts.push(T.skipped_prefix + ' ' + sk.join(', ') + '.');
    for (const [key, n] of rep.problems) {
        const [code, ...rest] = key.split(':');
        const detail = rest.join(':');
        const base = T['err_' + code] || T.err_read_failed;
        parts.push(fmt(base, { syntax: detail, n }));
    }
    if (!rep.frames.length && !rep.volume && s.viewerExe && !rep.problems.size) parts.push(T.hint_viewer_only);
    return parts.join(' ');
}

// ── текущая картинка ────────────────────────────────────────────────────

function currentImage() {
    if (state.mode === 'volume' && state.volume && state.pano && (state.plane === 'panorama' || state.plane === 'cross')) {
        const v = state.volume, P = state.pano;
        const raw = { slope: v.slope, intercept: v.intercept, mono1: v.mono1 };
        if (state.plane === 'panorama' && P.edit) {
            return { data: P.mip, w: v.w, h: v.h, samples: 1, sx: v.spacing[0], sy: v.spacing[1], raw, key: 'pm|' + P.version };
        }
        if (state.plane === 'panorama') {
            return { ...P.img, samples: 1, raw, key: 'p|' + P.version };
        }
        if (!P.cross || P.cross.at !== P.arcIndex || P.cross.version !== P.version) {
            P.cross = { ...crossSection(v, P.img.curve, P.arcIndex, 20, P.img.z0, P.img.z1), at: P.arcIndex, version: P.version };
        }
        return { ...P.cross, samples: 1, raw, key: 'c|' + P.arcIndex + '|' + P.version };
    }
    if (state.mode === 'volume' && state.volume) {
        const v = state.volume;
        const img = planeImage(v, state.plane, state.index);
        img.raw = { slope: v.slope, intercept: v.intercept, mono1: v.mono1 };
        img.key = 'v|' + state.plane + '|' + state.index;
        return img;
    }
    const f = state.frames[state.index];
    if (!f) return null;
    const sp = f.meta?.pixelSpacing;
    return {
        data: f.data, w: f.w, h: f.h, samples: f.samples,
        sx: sp ? sp[1] : 0, sy: sp ? sp[0] : 0,
        detector: !!f.meta?.spacingIsDetector,
        raw: { slope: f.meta?.slope || 1, intercept: f.meta?.intercept || 0, mono1: f.meta?.photometric === 'MONOCHROME1' },
        key: 'f|' + state.index,
    };
}

function applyAutoWindow(fromFile) {
    const img = currentImage();
    if (!img) return;
    const meta = state.mode === 'volume' ? state.volume : state.frames[state.index]?.meta;
    let w = null;
    // Окно из файла — только если оно правдоподобно для этих данных.
    if (fromFile && meta && meta.windowWidth > 1 && meta.windowCenter != null) {
        const [lo, hi] = valueRange(img, img.raw);
        if (meta.windowCenter > lo && meta.windowCenter < hi) w = { center: meta.windowCenter, width: meta.windowWidth };
    }
    if (!w) w = autoWindow(img, img.raw);
    state.win.center = w.center;
    state.win.width = w.width;
    syncRanges(img);
}

function syncRanges(img) {
    img = img || currentImage();
    if (!img) return;
    const [min, max] = img.samples === 3 ? [0, 255] : valueRange(img, img.raw);
    const c = $('#dvCenter'), w = $('#dvWidth');
    c.min = String(min); c.max = String(Math.max(min + 1, max));
    w.min = '1'; w.max = String(Math.max(2, (max - min) * 2));
    c.value = String(Math.round(state.win.center));
    w.value = String(Math.round(state.win.width));
}

// ── отрисовка ───────────────────────────────────────────────────────────

/** Где картинка лежит на холсте (в CSS-пикселях обёртки). */
function layout(img) {
    const wrap = $('#dvCanvasWrap');
    const W = wrap.clientWidth || 600;
    const aspectW = img.w * (img.sx || 1), aspectH = img.h * (img.sy || 1);
    const maxH = Math.max(240, Math.min(window.innerHeight * 0.75, 900));
    const fit = Math.min(W / aspectW, maxH / aspectH);
    const H = Math.round(Math.min(maxH, aspectH * fit));
    const dw = aspectW * fit * state.zoom, dh = aspectH * fit * state.zoom;
    const x = (W - dw) / 2 + state.pan.x, y = (H - dh) / 2 + state.pan.y;
    return { W, H, x, y, dw, dh, kx: dw / img.w, ky: dh / img.h };
}

function rendered(img) {
    const key = img.key + '|' + state.win.center + '|' + state.win.width + '|' + state.win.invert;
    if (cache.key === key && cache.canvas) return cache.canvas;
    const c = cache.canvas && cache.canvas.width === img.w && cache.canvas.height === img.h
        ? cache.canvas : Object.assign(document.createElement('canvas'), { width: img.w, height: img.h });
    const ctx = c.getContext('2d');
    const id = ctx.createImageData(img.w, img.h);
    paint(img, state.win, img.raw, id.data);
    ctx.putImageData(id, 0, 0);
    cache = { key, canvas: c, img };
    return c;
}

let rafPending = false;
function draw() {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(() => { rafPending = false; drawNow(); });
}

function drawNow() {
    const img = currentImage();
    if (!img) return;
    const L = layout(img);
    const canvas = $('#dvCanvas');
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    canvas.style.height = L.H + 'px';
    canvas.width = Math.round(L.W * dpr);
    canvas.height = Math.round(L.H * dpr);
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, L.W, L.H);
    ctx.imageSmoothingEnabled = L.kx < 3;
    ctx.drawImage(rendered(img), L.x, L.y, L.dw, L.dh);

    drawCrosshair(ctx, img, L);
    drawMeasure(ctx, L);
    renderInfo(img);
    positionOverlay(L);
    canvas.style.touchAction = state.zoom > 1.01 || tool === 'measure' ? 'none' : 'pan-y';
}

function drawCrosshair(ctx, img, L) {
    if (state.mode !== 'volume' || !state.cursor) return;
    const c = state.cursor, v = state.volume;
    const P = state.pano;
    if (P && state.plane === 'panorama' && P.edit) { drawArchEditor(ctx, L); return; }
    if (P && (state.plane === 'panorama' || state.plane === 'cross')) {
        const sy = L.y + (P.img.z1 - c.z + 0.5) * L.ky;
        const sx = state.plane === 'panorama' ? L.x + (P.arcIndex + 0.5) * L.kx : L.x + L.dw / 2;
        line(ctx, L.x, sy, L.x + L.dw, sy);
        line(ctx, sx, L.y, sx, L.y + L.dh);
        return;
    }
    if (P && state.plane === 'axial') drawArchOnAxial(ctx, L, P);
    let px, py;
    if (state.plane === 'axial') { px = c.x; py = c.y; }
    else if (state.plane === 'coronal') { px = c.x; py = v.d - 1 - c.z; }
    else { px = c.y; py = v.d - 1 - c.z; }
    const sx = L.x + (px + 0.5) * L.kx, sy = L.y + (py + 0.5) * L.ky;
    ctx.save();
    ctx.strokeStyle = 'rgba(250, 204, 21, .55)';
    ctx.lineWidth = 1;
    ctx.setLineDash([6, 5]);
    ctx.beginPath();
    ctx.moveTo(L.x, sy); ctx.lineTo(L.x + L.dw, sy);
    ctx.moveTo(sx, L.y); ctx.lineTo(sx, L.y + L.dh);
    ctx.stroke();
    ctx.restore();
}

function line(ctx, x1, y1, x2, y2) {
    ctx.save();
    ctx.strokeStyle = 'rgba(250, 204, 21, .55)';
    ctx.lineWidth = 1;
    ctx.setLineDash([6, 5]);
    ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
    ctx.restore();
}

/** Дуга панорамы на срезе «сверху» и отметка поперечного среза — чтобы было видно, откуда панорама. */
function drawArchOnAxial(ctx, L, P) {
    const pts = sampleCurve(P.points, 3);
    ctx.save();
    ctx.strokeStyle = 'rgba(56, 189, 248, .7)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    pts.forEach((p, i) => { const x = L.x + p.x * L.kx, y = L.y + p.y * L.ky; i ? ctx.lineTo(x, y) : ctx.moveTo(x, y); });
    ctx.stroke();
    const c = P.img.curve[P.arcIndex];
    if (c) {
        const k = 20 / Math.min(state.volume.spacing[0], state.volume.spacing[1]);
        ctx.strokeStyle = '#facc15';
        ctx.beginPath();
        ctx.moveTo(L.x + (c.x - c.nx * k) * L.kx, L.y + (c.y - c.ny * k) * L.ky);
        ctx.lineTo(L.x + (c.x + c.nx * k) * L.kx, L.y + (c.y + c.ny * k) * L.ky);
        ctx.stroke();
    }
    ctx.restore();
}

/** Правка дуги: линия и точки, которые тащат пальцем. */
function drawArchEditor(ctx, L) {
    const P = state.pano;
    const pts = sampleCurve(P.points, 2);
    ctx.save();
    ctx.strokeStyle = '#38bdf8';
    ctx.lineWidth = 2;
    ctx.beginPath();
    pts.forEach((p, i) => { const x = L.x + p.x * L.kx, y = L.y + p.y * L.ky; i ? ctx.lineTo(x, y) : ctx.moveTo(x, y); });
    ctx.stroke();
    for (const p of P.points) {
        ctx.beginPath();
        ctx.arc(L.x + p.x * L.kx, L.y + p.y * L.ky, 7, 0, Math.PI * 2);
        ctx.fillStyle = '#facc15';
        ctx.fill();
        ctx.lineWidth = 2;
        ctx.strokeStyle = 'rgba(0,0,0,.7)';
        ctx.stroke();
    }
    ctx.restore();
}

function drawMeasure(ctx, L) {
    const m = state.measure;
    if (!m || !m.to) return;
    const a = { x: L.x + m.from.x * L.kx, y: L.y + m.from.y * L.ky };
    const b = { x: L.x + m.to.x * L.kx, y: L.y + m.to.y * L.ky };
    ctx.save();
    ctx.strokeStyle = '#38bdf8';
    ctx.fillStyle = '#38bdf8';
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
    for (const p of [a, b]) { ctx.beginPath(); ctx.arc(p.x, p.y, 4, 0, Math.PI * 2); ctx.fill(); }
    const t = measureText();
    if (t) {
        ctx.font = '600 13px system-ui, sans-serif';
        const tx = (a.x + b.x) / 2 + 8, ty = (a.y + b.y) / 2 - 8;
        ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(0,0,0,.75)';
        ctx.strokeText(t, tx, ty);
        ctx.fillText(t, tx, ty);
    }
    ctx.restore();
}

function measureText() {
    const m = state.measure;
    const img = cache.img || currentImage();
    if (!m || !m.to || !img) return '';
    const dx = m.to.x - m.from.x, dy = m.to.y - m.from.y;
    if (!img.sx || !img.sy) return fmt(T.measure_px, { n: Math.round(Math.hypot(dx, dy)) });
    const mm = Math.hypot(dx * img.sx, dy * img.sy);
    return fmt(img.detector ? (T.measure_mm_approx || T.measure_mm) : T.measure_mm, { n: mm.toFixed(1) });
}

function renderInfo(img) {
    const bits = [];
    if (state.mode === 'volume' && state.pano && (state.plane === 'panorama' || state.plane === 'cross')) {
        const v = state.volume, P = state.pano;
        bits.push(T['plane_' + state.plane] || state.plane);
        if (!P.edit) bits.push(fmt(T.pano_length, { mm: Math.round(P.img.w * P.img.sx) }));
        if (v.meta?.studyDate) bits.push(formatDicomDate(v.meta.studyDate));
    } else if (state.mode === 'volume') {
        const v = state.volume;
        bits.push(T['plane_' + state.plane] || state.plane);
        bits.push(fmt(T.slice_n, { n: state.index + 1, of: planeDepth(v, state.plane) }));
        if (v.meta?.studyDate) bits.push(formatDicomDate(v.meta.studyDate));
    } else {
        const f = state.frames[state.index];
        if (f?.meta?.modality) bits.push(f.meta.modality);
        if (f?.meta?.studyDate) bits.push(formatDicomDate(f.meta.studyDate));
        if (state.frames.length > 1) bits.push(fmt(T.slice_n, { n: state.index + 1, of: state.frames.length }));
    }
    bits.push(img.w + '×' + img.h);
    $('#dvInfo').textContent = bits.join(' · ');
    const t = measureText();
    $('#dvMeasure').textContent = t;
    $('#dvMeasure').hidden = !t;
}

function formatDicomDate(s) {
    if (!/^\d{8}$/.test(s)) return s;
    return `${s.slice(6, 8)}.${s.slice(4, 6)}.${s.slice(0, 4)}`;
}

// ── плоскости и срезы ───────────────────────────────────────────────────

function setPlane(p) {
    // «Снимки» — отдельные кадры с того же диска (панорама рядом с КТ).
    if (p === 'frames') {
        if (state.mode === 'frames' || !state.frames.length) return;
        state.mode = 'frames';
        state.index = Math.min(state.index, state.frames.length - 1);
        if (state.index < 0 || state.index >= state.frames.length) state.index = 0;
    } else if (p === 'panorama' || p === 'cross') {
        if (!state.volume) return;
        if (state.mode === 'volume' && state.plane === p) return;
        if (!state.pano) {
            // Поиск дуги — доли секунды счёта: сначала даём странице показать
            // «Строю панораму…», потом считаем.
            say(T.pano_building);
            setTimeout(() => { if (ensurePano()) setPlane(p); }, 30);
            return;
        }
        state.mode = 'volume';
        state.plane = p;
        state.index = state.pano.arcIndex;
    } else {
        if (!state.volume) return;
        if (state.mode === 'volume' && state.plane === p) return;
        state.mode = 'volume';
        state.plane = p;
        const c = state.cursor;
        state.index = p === 'axial' ? c.z : p === 'coronal' ? c.y : c.x;
    }
    state.zoom = 1; state.pan = { x: 0, y: 0 }; state.measure = null;
    cache.key = '';
    syncPlaneButtons();
    syncSlider();
    applyAutoWindow(state.mode === 'frames');
    draw();
    syncChartShape();
    if (ai) ai.refresh();
}

function syncPlaneButtons() {
    const current = state.mode === 'frames' ? 'frames' : state.plane;
    $$('[data-plane]').forEach(b => {
        const on = b.dataset.plane === current;
        b.classList.toggle('on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    const panoMode = state.mode === 'volume' && (state.plane === 'panorama' || state.plane === 'cross');
    if ($('#dvPanoBox')) $('#dvPanoBox').hidden = !panoMode;
    const cb = $('[data-plane="cross"]');
    if (cb) cb.hidden = !state.pano;
    const fb = $('[data-plane="frames"]');
    if (fb) {
        fb.hidden = !(state.volume && state.frames.length);
        fb.textContent = fmt(T.plane_frames, { n: state.frames.length });
    }
}

function depthNow() {
    if (state.mode !== 'volume') return state.frames.length;
    if ((state.plane === 'panorama' || state.plane === 'cross') && state.pano) return state.pano.img.w;
    return planeDepth(state.volume, state.plane);
}

function syncSlider() {
    const depth = depthNow();
    $('#dvSliceRow').hidden = depth < 2;
    $('#dvSlice').max = String(Math.max(0, depth - 1));
    $('#dvSlice').value = String(state.index);
}

function setIndex(i) {
    const depth = depthNow();
    const next = Math.max(0, Math.min(depth - 1, Math.round(i)));
    if (next === state.index) return;
    const switchedKind = state.mode === 'frames' && state.frames[next]?.samples !== state.frames[state.index]?.samples;
    state.index = next;
    if (state.mode === 'volume' && state.pano && (state.plane === 'panorama' || state.plane === 'cross')) {
        setArcIndex(next);
    } else if (state.mode === 'volume') {
        const c = state.cursor;
        if (state.plane === 'axial') c.z = next; else if (state.plane === 'coronal') c.y = next; else c.x = next;
    } else {
        state.measure = null;
        if (switchedKind) applyAutoWindow(true);
    }
    $('#dvSlice').value = String(next);
    draw();
}

/** Точка на текущей плоскости → перекрестие во всех трёх. */
function setCursorFrom(p) {
    const v = state.volume, c = state.cursor;
    if (state.pano && state.plane === 'panorama' && !state.pano.edit) {
        const P = state.pano;
        setArcIndex(Math.round(p.x));
        c.z = Math.max(0, Math.min(v.d - 1, P.img.z1 - Math.round(p.y)));
        $('#dvSlice').value = String(P.arcIndex);
        draw();
        return;
    }
    if (state.pano && state.plane === 'cross') {
        c.z = Math.max(0, Math.min(v.d - 1, state.pano.img.z1 - Math.round(p.y)));
        draw();
        return;
    }
    if (state.pano && state.pano.edit) return;
    const ix = Math.max(0, Math.floor(p.x)), iy = Math.max(0, Math.floor(p.y));
    if (state.plane === 'axial') { c.x = Math.min(v.w - 1, ix); c.y = Math.min(v.h - 1, iy); }
    else if (state.plane === 'coronal') { c.x = Math.min(v.w - 1, ix); c.z = v.d - 1 - Math.min(v.d - 1, iy); }
    else { c.y = Math.min(v.h - 1, ix); c.z = v.d - 1 - Math.min(v.d - 1, iy); }
    draw();
}

// ── жесты ───────────────────────────────────────────────────────────────

const pointers = new Map();
let gesture = null;

function setTool(t) {
    tool = t;
    $$('[data-tool]').forEach(b => {
        const on = b.dataset.tool === t;
        b.classList.toggle('on', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    $('#dvCanvas').style.cursor = t === 'measure' ? 'crosshair' : 'grab';
    draw();
}

/** Точка экрана → пиксель картинки. */
function toImage(e) {
    const img = cache.img || currentImage();
    const r = $('#dvCanvas').getBoundingClientRect();
    const L = layout(img);
    return { x: (e.clientX - r.left - L.x) / L.kx, y: (e.clientY - r.top - L.y) / L.ky };
}

function onDown(e) {
    $('#dvCanvas').focus({ preventScroll: true });
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    try { $('#dvCanvas').setPointerCapture(e.pointerId); } catch (_) {}
    if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        gesture = { kind: 'pinch', dist: Math.hypot(a.x - b.x, a.y - b.y), zoom: state.zoom, pan: { ...state.pan }, mid: mid(a, b) };
        if (state.measure && !state.measure.to) state.measure = null;
        return;
    }
    if (state.pano && state.pano.edit && state.plane === 'panorama' && state.mode === 'volume') {
        const idx = nearestArchPoint(e);
        if (idx >= 0) { gesture = { kind: 'arch', idx }; return; }
    }
    if (tool === 'measure') {
        const p = toImage(e);
        state.measure = { from: p, to: null };
        gesture = { kind: 'measure' };
    } else {
        gesture = { kind: 'pan', x: e.clientX, y: e.clientY, pan: { ...state.pan }, moved: false };
    }
}

function onMove(e) {
    if (!pointers.has(e.pointerId)) return;
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (!gesture) return;
    if (gesture.kind === 'pinch' && pointers.size >= 2) {
        const [a, b] = [...pointers.values()];
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        const z = Math.max(0.5, Math.min(12, gesture.zoom * dist / Math.max(1, gesture.dist)));
        zoomAt(gesture.mid, z, gesture);
        return;
    }
    if (gesture.kind === 'measure') {
        state.measure.to = toImage(e);
        draw();
        return;
    }
    if (gesture.kind === 'arch') {
        const p = toImage(e), v = state.volume;
        state.pano.points[gesture.idx] = { x: Math.max(0, Math.min(v.w - 1, p.x)), y: Math.max(0, Math.min(v.h - 1, p.y)) };
        gesture.moved = true;
        draw();
        return;
    }
    if (gesture.kind === 'pan') {
        const dx = e.clientX - gesture.x, dy = e.clientY - gesture.y;
        if (!gesture.moved && Math.hypot(dx, dy) < 6) return;
        gesture.moved = true;
        state.pan.x = gesture.pan.x + dx;
        state.pan.y = gesture.pan.y + dy;
        draw();
    }
}

function onUp(e) {
    const was = gesture;
    pointers.delete(e.pointerId);
    try { $('#dvCanvas').releasePointerCapture(e.pointerId); } catch (_) {}
    if (pointers.size === 0 && was && was.kind === 'arch') {
        // Точку отпустили — панорама собирается по новой линии.
        gesture = null;
        if (was.moved) { state.pano.manual = true; rebuildPano(true); }
        return;
    }
    if (pointers.size === 0) {
        // Касание без движения в режиме «двигать» на КТ — поставить перекрестие.
        if (was && was.kind === 'pan' && !was.moved && e.type === 'pointerup' && state.mode === 'volume') {
            setCursorFrom(toImage(e));
        }
        gesture = null;
    } else if (pointers.size === 1 && was && was.kind === 'pinch') {
        const [p] = [...pointers.values()];
        gesture = { kind: 'pan', x: p.x, y: p.y, pan: { ...state.pan }, moved: true };
    }
}

function nearestArchPoint(e) {
    const r = $('#dvCanvas').getBoundingClientRect();
    const img = cache.img || currentImage();
    const L = layout(img);
    let best = -1, bd = 22;
    state.pano.points.forEach((p, i) => {
        const d = Math.hypot(r.left + L.x + p.x * L.kx - e.clientX, r.top + L.y + p.y * L.ky - e.clientY);
        if (d < bd) { bd = d; best = i; }
    });
    return best;
}

// ── панорама из томографии ──────────────────────────────────────────────

/**
 * Дуга ищется один раз на объём (зубы — самое плотное, их проекция сверху —
 * подкова), панорама пересобирается при смене толщины слоя или правке дуги.
 */
function ensurePano() {
    if (state.pano) return true;
    const v = state.volume;
    say(T.pano_building);
    const t0 = performance.now();
    let arch;
    try {
        arch = detectArch(v);
    } catch (e) {
        if (window.__handleError) window.__handleError(e, 'pano_detect', { say: T.pano_auto_failed });
        return false;
    }
    const thick = +($('#dvPanoThick')?.value || 14);
    state.pano = {
        points: arch.points.map(p => ({ ...p })),
        window: arch.window,
        thick,
        edit: !arch.ok,
        manual: false,
        auto: arch.ok,
        mip: arch.mip || axialMip(v, arch.zFrom, arch.zTo),
        version: 0,
        arcIndex: 0,
        img: null,
        cross: null,
    };
    rebuildPano(false);
    state.pano.arcIndex = Math.floor(state.pano.img.w / 2);
    if ($('#dvPanoEdit')) $('#dvPanoEdit').checked = state.pano.edit;
    if ($('#dvPanoEditHint')) $('#dvPanoEditHint').hidden = !state.pano.edit;
    if ($('#dvPanoThickVal')) $('#dvPanoThickVal').textContent = thick;
    say(arch.ok ? '' : T.pano_auto_failed, arch.ok ? '' : 'warn');
    if (window.__ae) window.__ae('feature_use', { f: 'pano_built', auto: arch.ok ? 1 : 0, ms: Math.round(performance.now() - t0), w: state.pano.img.w });
    return true;
}

let panoRaf = 0;
function rebuildPano(now) {
    const go = () => {
        panoRaf = 0;
        const P = state.pano;
        if (!P) return;
        P.img = renderPanorama(state.volume, P.points, { thicknessMm: P.thick, ...P.window });
        P.version++;
        P.arcIndex = Math.max(0, Math.min(P.img.w - 1, P.arcIndex));
        P.cross = null;
        cache.key = '';
        syncSlider();
        draw();
    };
    if (now || !state.pano.img) { go(); return; }
    if (!panoRaf) panoRaf = requestAnimationFrame(go);
}

/** Точка вдоль дуги → перекрестие во всех плоскостях. */
function setArcIndex(i) {
    const P = state.pano;
    P.arcIndex = Math.max(0, Math.min(P.img.w - 1, Math.round(i)));
    state.index = P.arcIndex;
    const c = P.img.curve[P.arcIndex];
    if (c) {
        state.cursor.x = Math.max(0, Math.min(state.volume.w - 1, Math.round(c.x)));
        state.cursor.y = Math.max(0, Math.min(state.volume.h - 1, Math.round(c.y)));
    }
}

function mid(a, b) {
    const r = $('#dvCanvas').getBoundingClientRect();
    return { x: (a.x + b.x) / 2 - r.left, y: (a.y + b.y) / 2 - r.top };
}

/** Масштаб вокруг точки экрана: то, что под пальцами, остаётся под пальцами. */
function zoomAt(pt, z, base) {
    const img = currentImage();
    const z0 = base ? base.zoom : state.zoom;
    const pan0 = base ? base.pan : state.pan;
    state.zoom = z0; state.pan = { ...pan0 };
    const L0 = layout(img);
    const ix = (pt.x - L0.x) / L0.kx, iy = (pt.y - L0.y) / L0.ky;
    state.zoom = z;
    const L1 = layout(img);
    state.pan.x += pt.x - (L1.x + ix * L1.kx);
    state.pan.y += pt.y - (L1.y + iy * L1.ky);
    draw();
}

function onWheel(e) {
    e.preventDefault();
    const depthOk = state.mode === 'volume' || state.frames.length > 1;
    // Shift + колесо — срезы. С Shift часть систем шлёт deltaX вместо deltaY.
    if (e.shiftKey) {
        if (depthOk) setIndex(state.index + Math.sign(e.deltaY || e.deltaX));
        return;
    }
    const r = $('#dvCanvas').getBoundingClientRect();
    const z = Math.max(0.5, Math.min(12, state.zoom * (e.deltaY < 0 ? 1.15 : 1 / 1.15)));
    zoomAt({ x: e.clientX - r.left, y: e.clientY - r.top }, z);
}

function onKey(e) {
    if (e.key === 'ArrowUp' || e.key === 'PageUp') { setIndex(state.index + 1); e.preventDefault(); }
    else if (e.key === 'ArrowDown' || e.key === 'PageDown') { setIndex(state.index - 1); e.preventDefault(); }
    else if (e.key === '+' || e.key === '=') { zoomAt(centerPt(), Math.min(12, state.zoom * 1.25)); }
    else if (e.key === '-') { zoomAt(centerPt(), Math.max(0.5, state.zoom / 1.25)); }
    else if ((window.__keyLetter ? window.__keyLetter(e) : e.key) === '0' && !e.ctrlKey && !e.metaKey && !e.altKey) resetView();
}

function centerPt() {
    const c = $('#dvCanvas');
    return { x: c.clientWidth / 2, y: c.clientHeight / 2 };
}

function resetView() {
    state.zoom = 1; state.pan = { x: 0, y: 0 }; state.measure = null;
    draw();
}

// ── наложение карты зубов ───────────────────────────────────────────────

/**
 * То, ради чего пациент сюда приходит: увидеть НОМЕРА на своём снимке.
 * Схема привязана к КАРТИНКЕ, а не к окну: приблизил снимок — схема
 * приблизилась вместе с ним, и номер остаётся над своим зубом.
 */
function toggleOverlay() {
    state.overlay = $('#dvOverlay').checked;
    $('#dvOverlayBox').hidden = !state.overlay;
    $('#dvOverlayWrap').hidden = !state.overlay;

    if (state.overlay && !chart) {
        if (!state.overlayTouched) setOverlayBox(OVERLAY_DEFAULT[chartShape()]);
        chart = new Odontogram($('#dvChart'), {
            teeth: CFG.teeth,
            dentition: 'permanent',
            surfaces: false,
            shape: chartShape(),
            labels: { names: CFG.names, chartLabel: T.chart_label },
            onPick: ({ fdi }) => {
                $('#dvPicked').textContent = CFG.names[fdi] + ' · ' + fdi;
                $('#dvPicked').hidden = false;
            },
        });
    }
    positionOverlay();
    if (ai) ai.refresh();
}

/*
 * Раскладка схемы — под то, что на экране. Срез КТ сверху показывает дуги
 * такими, как их рисует карта врача; панорама (и снимок, и панорама из КТ)
 * показывает ту же дугу РАЗВЁРНУТОЙ в два ряда. Дуга поверх панорамы ставила
 * номера мимо зубов — резцы у носа, моляры на ветвях челюсти.
 */
const OVERLAY_DEFAULT = {
    arch: { x: 0.08, y: 0.18, w: 0.84 },
    panoramic: { x: 0.15, y: 0.26, w: 0.70 },
};

function chartShape() {
    return state.mode === 'volume' && state.plane === 'axial' ? 'arch' : 'panoramic';
}

function setOverlayBox(box) {
    state.overlayBox = { ...box };
    $$('[data-overlay]').forEach(el => {
        const k = el.dataset.overlay;
        if (k in state.overlayBox) el.value = String(Math.round(state.overlayBox[k] * 100));
    });
}

function syncChartShape() {
    if (!chart) return;
    const shape = chartShape();
    if (chart.shape === shape) return;
    chart.setShape(shape);
    // Подгонку, сделанную человеком, не трогаем; умолчание — своё у каждой раскладки.
    if (!state.overlayTouched) setOverlayBox(OVERLAY_DEFAULT[shape]);
    positionOverlay();
}

function positionOverlay(L) {
    const wrap = $('#dvOverlayWrap');
    if (!wrap || wrap.hidden) return;
    const img = cache.img || currentImage();
    if (!img) return;
    L = L || layout(img);
    const b = state.overlayBox;
    wrap.style.insetInlineStart = '';
    wrap.style.left = (L.x + b.x * L.dw) + 'px';
    wrap.style.top = (L.y + b.y * L.dh) + 'px';
    wrap.style.width = (b.w * L.dw) + 'px';
    wrap.style.opacity = String($('#dvOverlayAlpha').value / 100);
}

// ── ИИ-разбор: что уходит на сервер ─────────────────────────────────────

/** Что за снимок — подсказка для выбора в панели (человек может поправить). */
function aiKindGuess() {
    if (state.mode === 'volume') return state.plane === 'panorama' ? 'cbct_panoramic' : 'cbct_slice';
    const f = state.frames[state.index];
    if (!f) return 'unknown';
    const m = f.meta?.modality;
    if (m === 'PX') return 'panoramic';
    if (m === 'IO') return 'periapical';
    // Панорама шире 3:2 (обрезанный скриншот — около 1,5), а прицельные и «вприкус» — 4:3 и уже.
    return f.w / f.h >= 1.45 ? 'panoramic' : 'unknown';
}

function aiNumbered() {
    return state.preNumbered || (state.overlay && !$('#dvOverlayWrap').hidden && !(state.pano && state.pano.edit && state.plane === 'panorama'));
}

function aiPeek() {
    const img = currentImage();
    if (!img) return null;
    return { numbered: aiNumbered(), kind: aiKindGuess() };
}

/**
 * Копия видимого снимка для ИИ: окно яркости как на экране, без масштаба и
 * сдвига, с настоящими пропорциями; длинная сторона 1600–2048 px. Если схема
 * зубов совмещена — номера FDI рисуются в центрах зубов схемы: человек
 * совместил её глазами, и номер верен по построению (модели путают номера).
 */
async function aiSnapshot() {
    const img = currentImage();
    if (!img) return null;
    const src = rendered(img);
    const aw = img.w * (img.sx || 1), ah = img.h * (img.sy || 1);
    const long = Math.max(aw, ah);
    const k = long < 1600 ? 1600 / long : Math.min(1, 2048 / long);
    const W = Math.round(aw * k), H = Math.round(ah * k);
    const out = document.createElement('canvas');
    out.width = W; out.height = H;
    const ctx = out.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(src, 0, 0, W, H);

    const numbered = aiNumbered();
    if (numbered && !state.preNumbered) {
        const L = layout(img);
        const cr = $('#dvCanvas').getBoundingClientRect();
        const font = Math.max(12, Math.round(W / 70));
        ctx.font = `700 ${font}px system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.lineJoin = 'round';
        document.querySelectorAll('#dvChart .og-tooth[data-fdi]').forEach(t => {
            const r = t.getBoundingClientRect();
            const ix = (r.left + r.width / 2 - cr.left - L.x) / L.kx;
            const iy = (r.top + r.height / 2 - cr.top - L.y) / L.ky;
            if (ix < 0 || iy < 0 || ix > img.w || iy > img.h) return;
            const x = ix / img.w * W, y = iy / img.h * H;
            ctx.lineWidth = Math.max(3, font / 4);
            ctx.strokeStyle = 'rgba(0,0,0,.85)';
            ctx.strokeText(t.dataset.fdi, x, y);
            ctx.fillStyle = '#fff';
            ctx.fillText(t.dataset.fdi, x, y);
        });
    }
    const blob = await window.__canvasBlob(out, 'image/jpeg', 0.9);
    if (!blob) return null;
    return { blob, dataUrl: out.toDataURL('image/jpeg', 0.85), numbered, w: W, h: H, kind: aiKindGuess() };
}

/** Снимок, отложенный до оплаты токенов, — обратно в просмотрщик. */
async function openStashed(dataUrl, numbered) {
    const blob = await (await fetch(dataUrl)).blob();
    const bmp = await createImageBitmap(blob);
    const frame = fromRgba(bitmapRgba(bmp), bmp.width, bmp.height, 'xray');
    bmp.close && bmp.close();
    state.frames = [frame];
    state.volume = null;
    state.pano = null;
    state.mode = 'frames';
    state.index = 0;
    state.zoom = 1; state.pan = { x: 0, y: 0 }; state.measure = null;
    cache.key = '';
    $('#dvWork').hidden = false;
    $('#dvPlanes').hidden = true;
    syncSlider();
    applyAutoWindow(true);
    state.preNumbered = !!numbered;
    draw();
    if (ai) ai.refresh();
}

// ── сохранение ──────────────────────────────────────────────────────────

/**
 * Сохраняется ровно то, что видно: снимок с окном и масштабом, линейка и —
 * до 2026-10-08 этого не было, хотя подсказка обещала — наложенная схема.
 * Схема живёт в SVG поверх холста, и прежнее сохранение брало только холст.
 */
async function save() {
    const canvas = $('#dvCanvas');
    const out = document.createElement('canvas');
    out.width = canvas.width; out.height = canvas.height;
    const ctx = out.getContext('2d');
    ctx.drawImage(canvas, 0, 0);

    if (state.overlay && !$('#dvOverlayWrap').hidden) {
        try {
            const dpr = canvas.width / canvas.clientWidth;
            const svg = $('#dvChart svg');
            const wrap = $('#dvOverlayWrap');
            const clone = svg.cloneNode(true);
            const css = '.og-body{fill:none;stroke:#38bdf8;stroke-width:1.6}.og-num{font:700 12px system-ui,sans-serif;fill:#7dd3fc}';
            const st = document.createElementNS('http://www.w3.org/2000/svg', 'style');
            st.textContent = css;
            clone.insertBefore(st, clone.firstChild);
            const vb = svg.viewBox && svg.viewBox.baseVal;
            if (vb && vb.width) { clone.setAttribute('width', vb.width); clone.setAttribute('height', vb.height); }
            const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(clone)], { type: 'image/svg+xml' }));
            const im = new Image();
            await new Promise((res, rej) => { im.onload = res; im.onerror = rej; im.src = url; });
            const wr = wrap.getBoundingClientRect(), cr = canvas.getBoundingClientRect();
            ctx.globalAlpha = +$('#dvOverlayAlpha').value / 100;
            ctx.drawImage(im, (wr.left - cr.left) * dpr, (wr.top - cr.top) * dpr, wr.width * dpr, wr.height * dpr);
            ctx.globalAlpha = 1;
            URL.revokeObjectURL(url);
        } catch (e) {
            if (window.__handleError) window.__handleError(e, 'scan_save_overlay', { say: T.save_overlay_failed });
        }
    }

    const blob = await window.__canvasBlob(out, 'image/png');
    if (!blob) return;
    const f = state.mode === 'volume' ? null : state.frames[state.index];
    const base = ((f && f.name) || (state.mode === 'volume' ? 'ct-' + state.plane : 'scan')).replace(/\.[^.]+$/, '');
    const name = window.__outputName ? window.__outputName.join([base, 'view'], { ext: 'png' }) : base + '.png';
    window.__downloadFile(blob, name);
}

async function copyPdfText() {
    const t = $('#dvPdfText').textContent || '';
    try {
        await navigator.clipboard.writeText(t);
        window.__toast && window.__toast(T.copied, 'success');
    } catch (_) {
        const r = document.createRange();
        r.selectNodeContents($('#dvPdfText'));
        const s = window.getSelection(); s.removeAllRanges(); s.addRange(r);
    }
}

function say(text, kind) {
    const box = $('#dvSay');
    box.hidden = !text;
    box.textContent = text || '';
    box.className = 'dv-say' + (kind ? ' dv-say-' + kind : '');
}

function fmt(tpl, vars) {
    return String(tpl || '').replace(/:(\w+)/g, (m, k) => (k in vars ? String(vars[k]) : m));
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
